import type { Attributes, Context, Span, Tracer } from '@opentelemetry/api'
import {
  context,
  propagation,
  trace,
  SpanStatusCode,
  SpanKind,
} from '@opentelemetry/api'

interface StartSpanConfig {
  workName: string
  attributes?: Attributes
  spanKind?: SpanKind
  parentSpan?: Span
  setAsParentContext?: boolean
}

interface EndSpanConfig {
  status?: string | number
  code: SpanStatusCode
  message?: string
  endParentContext?: boolean
}

interface RequestSpanConfig {
  url: string
  attributes?: Attributes
}

interface PropagationOutput {
  traceparent: string
  tracestate: string
}

export const useOpenTelemetryStore = defineStore('openTelemetry.store', () => {
  let renderSpan: Undefinable<Span>
  let requestSpan: Undefinable<Span>
  let tracer: Undefinable<Tracer>
  let telemetryContext: Undefinable<Context>
  let parentSet: boolean

  /**
   * возращает tracer
   */
  const getTracer = () => {
    return trace.getTracer('frontend')
  }

  /**
   * устанавливает span запроса как родительский
   */
  const setRequestSpanAsParent = (): Context => {
    return trace.setSpan(context.active(), requestSpan!)
  }

  /**
   * устанавливает span рендера как родительский
   */
  const setRenderSpanAsParent = (): Context => {
    return trace.setSpan(context.active(), renderSpan!)
  }

  /**
   * устанавливает произвольный span как родительский
   * @param span
   */
  const setSpanAsParent = (span: Span): Context => {
    return trace.setSpan(context.active(), span)
  }

  /**
   * устанавливает запрос или рендерениг как контекст
   */

  const setMainContext = (): Context => {
    return renderSpan ? setRenderSpanAsParent() : setRequestSpanAsParent()
  }

  /**
   * возвращает output для propagation
   * https://opentelemetry.io/docs/instrumentation/js/propagation/
   */

  const getPropagationOutput = (): PropagationOutput => {
    const output = {}
    const context = parentSet ? telemetryContext : setMainContext()

    propagation.inject(context!, output)

    return output as PropagationOutput
  }

  /**
   *
   */
  const setSpanAttributes = (span: Span, attributes?: Attributes) => {
    attributes && span.setAttributes(attributes)
  }

  /**
   * создает span для выполнения любого кода
   * @param config
   */
  const startSpan = (config: StartSpanConfig) => {
    if (!process.server) return
    const { workName, attributes, spanKind, setAsParentContext } = config

    const parentContext = parentSet ? telemetryContext : setMainContext()
    const span = tracer?.startSpan(workName, { kind: spanKind }, parentContext)

    if (setAsParentContext && span) {
      parentSet = true
      telemetryContext = setSpanAsParent(span)
    } else {
      telemetryContext = setMainContext()
    }

    if (span) {
      setSpanAttributes(span, attributes)

      return {
        span,
        end(endConfig: EndSpanConfig) {
          endSpan(span, {
            ...endConfig,
            endParentContext: setAsParentContext,
          })
        },
      }
    }
  }

  /**
   * заканчивает запись span
   * @param span
   * @param config
   */
  const endSpan = (span: Span, config: EndSpanConfig) => {
    if (!process.server || !span) return
    const { status, code, message, endParentContext } = config

    switch (code) {
      case 1:
        span.setStatus({ code: SpanStatusCode.OK })
        break
      case 2:
        span.setStatus({ code: SpanStatusCode.ERROR, message })
        break
      default:
        span.setStatus({ code: SpanStatusCode.UNSET })
    }

    if (endParentContext) {
      parentSet = false
      telemetryContext = setMainContext()
    }

    status && span.setAttribute('http.status_code', status)
    span.end()
  }

  /**
   * начинает запись при запросе в nuxt
   * @param config
   */
  const startRequestSpan = (config: RequestSpanConfig) => {
    const { url, attributes } = config

    if (process.server) {
      tracer = getTracer()

      requestSpan = tracer.startSpan(`Page view`, {
        attributes: {
          'http.url': url,
          'http.method': 'GET',
        },
        kind: SpanKind.SERVER,
      })
      setSpanAttributes(requestSpan, attributes)
      telemetryContext = setRequestSpanAsParent()
    }
  }

  /**
   * заканчиавает запись, когда Nuxt отвечает клиенту
   */

  const endRequestSpan = (attributes?: Attributes) => {
    setSpanAttributes(requestSpan!, attributes)
    requestSpan?.end()
  }

  /**
   * начинает запись перед рендерингом html
   */

  const startRenderSpan = () => {
    if (process.server) {
      renderSpan = tracer!.startSpan(
        'Render',
        undefined,
        setRequestSpanAsParent(),
      )
    }
  }

  /**
   * заканчиавает запись, когда Nuxt отвечает клиенту
   */

  const endRenderSpan = (attributes?: Attributes) => {
    setSpanAttributes(requestSpan!, attributes)
    renderSpan?.end()
  }

  return {
    renderSpan,
    requestSpan,
    tracer,
    telemetryContext,
    getTracer,
    startRequestSpan,
    endRequestSpan,
    startRenderSpan,
    endRenderSpan,
    getPropagationOutput,
    startSpan,
  }
})
